import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import * as React from "react";
import { importCsvToKommo } from "../../../services/kommo.service";
import SectionTitle from "../../../ui-components/SectionTitle";
import { useToast } from "../../../ui-components/ToastProvider";

const FIELD_OPTIONS = [
  { value: "name", label: "Nome" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Telefone" },
  { value: "cpf", label: "CPF" },
  { value: "cnpj", label: "CNPJ" },
  { value: "instagram_url", label: "Instagram" },
  { value: "brand_name", label: "Nome da Marca" },
  { value: "brand_description", label: "Descrição da Marca" },
  { value: "brand_items", label: "Produtos" },
  { value: "city", label: "Cidade" },
  { value: "state", label: "Estado" },
  { value: "zipcode", label: "CEP" },
  { value: "address", label: "Endereço" },
  { value: "address_number", label: "Número" },
  { value: "address_complement", label: "Complemento" },
  { value: "district", label: "Bairro" },
];

function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
  for (const line of lines) {
    const cols: string[] = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        cols.push(current.trim());
        current = "";
      } else if (char === ";" && !inQuotes) {
        cols.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    cols.push(current.trim());
    if (cols.length > 0) rows.push(cols);
  }
  return rows;
}

export default function KommoImport() {
  const { showToast } = useToast();

  const [csvText, setCsvText] = React.useState("");
  const [rows, setRows] = React.useState<string[][]>([]);
  const [headers, setHeaders] = React.useState<string[]>([]);
  const [mapping, setMapping] = React.useState<Record<string, string>>({});
  const [isImporting, setIsImporting] = React.useState(false);
  const [result, setResult] = React.useState<{
    success: number;
    failed: number;
    errors: { row: number; email: string; error: string }[];
  } | null>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setCsvText(text);
      const parsed = parseCSV(text);
      if (parsed.length === 0) {
        showToast("CSV vazio ou inválido", { severity: "warning" });
        return;
      }

      const headerRow = parsed[0];
      const dataRows = parsed.slice(1);

      setHeaders(headerRow);
      setRows(dataRows);

      const autoMapping: Record<string, string> = {};
      headerRow.forEach((h) => {
        const lower = h.toLowerCase().trim();
        if (lower.includes("nome") || lower.includes("name")) autoMapping[h] = "name";
        else if (lower.includes("email") || lower.includes("e-mail")) autoMapping[h] = "email";
        else if (lower.includes("telefone") || lower.includes("phone") || lower.includes("celular")) autoMapping[h] = "phone";
        else if (lower === "cpf") autoMapping[h] = "cpf";
        else if (lower === "cnpj") autoMapping[h] = "cnpj";
        else if (lower.includes("instagram") || lower.includes("insta")) autoMapping[h] = "instagram_url";
        else if (lower.includes("marca") || lower.includes("brand")) autoMapping[h] = "brand_name";
        else if (lower.includes("descri")) autoMapping[h] = "brand_description";
        else if (lower.includes("produto") || lower.includes("items")) autoMapping[h] = "brand_items";
        else if (lower.includes("cidade") || lower.includes("city")) autoMapping[h] = "city";
        else if (lower.includes("estado") || lower.includes("state") || lower === "uf") autoMapping[h] = "state";
        else if (lower.includes("cep") || lower.includes("zip")) autoMapping[h] = "zipcode";
        else if (lower.includes("endereço") || lower.includes("endereco") || lower.includes("address")) autoMapping[h] = "address";
        else if (lower.includes("número") || lower.includes("numero") || lower.includes("number")) autoMapping[h] = "address_number";
        else if (lower.includes("complemento")) autoMapping[h] = "address_complement";
        else if (lower.includes("bairro") || lower.includes("district")) autoMapping[h] = "district";
        else autoMapping[h] = "";
      });
      setMapping(autoMapping);
      setResult(null);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleImport = async () => {
    const mappedRows = rows.map((row) => {
      const obj: any = {};
      headers.forEach((header, idx) => {
        const field = mapping[header];
        if (field) {
          obj[field] = row[idx] || "";
        }
      });
      return obj;
    });

    const validRows = mappedRows.filter((r: any) => r.name && r.email);
    if (validRows.length === 0) {
      showToast("Nenhuma linha com Nome e Email mapeados", { severity: "warning" });
      return;
    }

    if (validRows.length < mappedRows.length) {
      const skip = mappedRows.length - validRows.length;
      showToast(`${skip} linha(s) ignorada(s) por falta de Nome ou Email`, { severity: "info" });
    }

    setIsImporting(true);
    try {
      const res = await importCsvToKommo(validRows);
      setResult(res);
      if (res.failed === 0) {
        showToast(`${res.success} registros importados com sucesso!`, { severity: "success" });
      } else {
        showToast(`${res.success} sucesso, ${res.failed} falhas`, { severity: "warning" });
      }
    } catch (err: any) {
      showToast(err.message || "Erro ao importar", { severity: "error" });
    } finally {
      setIsImporting(false);
    }
  };

  const previewRows = rows.slice(0, 5);

  return (
    <Grid sx={{ p: 2 }}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <SectionTitle title="Kommo CRM — Importar CSV" />
        <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
          Faça upload de um arquivo CSV, mapeie as colunas e importe contatos e
          negócios para o Kommo.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            component="label"
            sx={{ backgroundColor: "#24324D" }}
          >
            Selecionar Arquivo CSV
            <input
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={handleFileUpload}
            />
          </Button>
        </Box>

        {headers.length > 0 && (
          <>
            <Typography sx={{ fontWeight: 600, mb: 2, color: "#24324D" }}>
              Mapeamento de Colunas
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mb: 2 }}>
              Para cada coluna do CSV, selecione o campo correspondente no Kommo.
              Campos obrigatórios: <strong>Nome</strong> e <strong>Email</strong>.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {headers.map((header) => (
                <Grid item xs={12} sm={6} md={4} key={header}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: mapping[header] ? "#24324D" : "#e2e8f0",
                      backgroundColor: mapping[header] ? "#f8fafc" : "#fff",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                      Coluna: <strong>{header}</strong>
                    </Typography>
                    <Select
                      value={mapping[header] || ""}
                      onChange={(e) =>
                        setMapping((prev) => ({
                          ...prev,
                          [header]: e.target.value as string,
                        }))
                      }
                      fullWidth
                      size="small"
                      displayEmpty
                      sx={{ mt: 0.5 }}
                    >
                      <MenuItem value="">-- Ignorar --</MenuItem>
                      {FIELD_OPTIONS.map((f) => (
                        <MenuItem key={f.value} value={f.value}>
                          {f.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Typography sx={{ fontWeight: 600, mb: 1, color: "#24324D" }}>
              Preview ({previewRows.length} de {rows.length} linhas)
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ mb: 3, maxHeight: 300 }}
            >
              <Table size="small" stickyHeader>
                <TableBody>
                  {previewRows.map((row, ri) => (
                    <TableRow key={ri}>
                      {row.map((cell, ci) => (
                        <TableCell
                          key={ci}
                          sx={{
                            maxWidth: 150,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="contained"
                onClick={handleImport}
                disabled={isImporting}
                startIcon={isImporting ? <CircularProgress size={16} /> : undefined}
                sx={{ backgroundColor: "#24324D" }}
              >
                {isImporting ? "Importando..." : `Importar ${rows.length} Registros`}
              </Button>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                {rows.length} linhas de dados detectadas
              </Typography>
            </Box>
          </>
        )}

        {result && (
          <Box sx={{ mt: 3 }}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 600, mb: 1, color: "#24324D" }}>
                Resultado da Importação
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Chip
                  label={`${result.success} sucesso`}
                  color="success"
                  variant="filled"
                />
                <Chip
                  label={`${result.failed} falhas`}
                  color={result.failed > 0 ? "error" : "default"}
                  variant="filled"
                />
              </Box>
              {result.errors.length > 0 && (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 300 }}
                >
                  <Table size="small" stickyHeader>
                    <TableBody>
                      {result.errors.map((err, idx) => (
                        <TableRow key={idx}>
                          <TableCell>Linha {err.row}</TableCell>
                          <TableCell>{err.email}</TableCell>
                          <TableCell sx={{ color: "#ef4444" }}>
                            {err.error}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        )}
      </Paper>
    </Grid>
  );
}
